import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { categories, getArticlesByCategory, getHotArticles } from "@/mocks/articles";
import type { Article } from "@/mocks/articles";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [currentSlug, setCurrentSlug] = useState(slug || "mingxing-bagua");
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    setCurrentSlug(slug || "mingxing-bagua");
  }, [slug]);

  useEffect(() => {
    setArticles(getArticlesByCategory(currentSlug));
  }, [currentSlug]);

  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const hotArticles = getHotArticles().slice(0, 5);

  const categoryTitle = currentCategory?.name || "分类";
  const categoryDesc = `${categoryTitle} - 51吃瓜官网为您提供最新${categoryTitle}爆料内容，吃瓜第一站！`;

  return (
    <>
      <SEOHead
        title={`${categoryTitle} - 51吃瓜官网 | 最新${categoryTitle}爆料黑料`}
        description={categoryDesc}
        keywords={`${categoryTitle},51吃瓜,51吃瓜官网,51吃瓜爆料,${categoryTitle}黑料,娱乐八卦`}
        canonicalUrl={`https://51chigua.com/category/${currentSlug}`}
        schemaData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${categoryTitle} - 51吃瓜官网`,
            description: categoryDesc,
            url: `https://51chigua.com/category/${currentSlug}`,
          },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: categoryTitle },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {categoryTitle}
            </h1>
            <p className="text-foreground-500 text-sm">
              共 {articles.length} 篇{categoryTitle}相关爆料，持续更新中...
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                    currentSlug === cat.slug
                      ? "bg-primary-500 text-white"
                      : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-8/12">
                {articles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {articles.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:w-4/12">
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 sticky top-24">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-500"></i>热门推荐
                  </h3>
                  <div className="flex flex-col gap-0">
                    {hotArticles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="group flex items-start gap-3 py-3 border-b border-background-200/70 last:border-0 cursor-pointer"
                      >
                        <span className="w-6 h-6 flex items-center justify-center rounded bg-primary-100 text-primary-600 text-xs font-bold flex-shrink-0 mt-0.5">
                          {index + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                            {article.title}
                          </p>
                          <span className="text-xs text-foreground-400 mt-1 block">{article.publishDate}</span>
                        </div>
                      </Link>
                    ))}
                  </div>

                  <div className="mt-6 pt-5 border-t border-background-200/70">
                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">热门标签</h3>
                    <div className="flex flex-wrap gap-2">
                      {["顶流男星", "税务问题", "网红翻车", "综艺冲突", "学历造假"].map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1.5 bg-background-50 text-sm text-foreground-600 rounded-full border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}